import React from 'react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Sparkles, MessageSquare, Edit, Trash2, ChevronLeft, Loader2, Plus } from 'lucide-react';

const ChatSidebar = ({
  collapsed, setCollapsed, conversations, currentConversation,
  renamingId, renameValue, setRenameValue, renameInputRef,
  onNewConversation, onLoadConversation, onDeleteConversation, onStartRename, onSubmitRename, onCancelRename,
}) => {
  // Group conversations by relative date bucket
  const now = Date.now();
  const buckets = { today: [], yesterday: [], week: [], older: [] };
  conversations.forEach((c) => {
    const diff = now - new Date(c.updated_at).getTime();
    const day = 24 * 60 * 60 * 1000;
    if (diff < day) buckets.today.push(c);
    else if (diff < 2 * day) buckets.yesterday.push(c);
    else if (diff < 7 * day) buckets.week.push(c);
    else buckets.older.push(c);
  });

  const sectionLabel = {
    today: 'Today',
    yesterday: 'Yesterday',
    week: 'Last 7 Days',
    older: 'Earlier',
  };

  return (
    <div
      className={`${collapsed ? 'w-0' : 'w-64'} border-r border-atelier-line bg-atelier-surface flex flex-col transition-all duration-300 overflow-hidden relative flex-shrink-0`}
      data-testid="chat-sidebar"
    >
      {!collapsed && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost" size="sm" onClick={() => setCollapsed(true)}
                className="absolute top-3.5 right-2.5 z-10 rounded-full text-atelier-subtle hover:text-atelier-ink hover:bg-atelier-surface2 h-7 w-7 p-0 transition-all duration-300"
                data-testid="collapse-sidebar-btn"
              >
                <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">收起側邊欄</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* ── Brand + new chat ── */}
      <div className="px-4 pt-4 pb-3 flex-shrink-0 border-b border-atelier-line">
        <div className="flex items-center gap-2.5 mb-4 pr-8">
          <div className="relative w-8 h-8 flex items-center justify-center">
            <div className="absolute inset-0 rounded-[10px] border border-atelier-lineHi" />
            <div className="w-2.5 h-2.5 rounded-sm bg-atelier-coral" />
          </div>
          <div className="flex items-baseline gap-2 min-w-0">
            <span
              className="text-[15px] truncate tracking-tight text-atelier-ink"
              style={{
                fontFamily: "'Noto Serif TC', 'Playfair Display', Georgia, serif",
                fontWeight: 500,
              }}
            >
              Reelscript
            </span>
            <span className="atelier-caption">AI</span>
          </div>
        </div>

        <button
          onClick={onNewConversation}
          className="w-full rounded-full h-10 text-[13px] flex items-center justify-center gap-1.5 bg-atelier-coral hover:bg-atelier-coralHi text-white transition-all duration-300"
          style={{ fontWeight: 500 }}
          data-testid="new-conversation-btn"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          新對話
        </button>
      </div>

      {/* ── Conversation list ── */}
      <div className="flex-1 overflow-y-auto sidebar-scroll py-3">
        {Object.entries(buckets).map(([key, list]) =>
          list.length > 0 ? (
            <div key={key} className="mb-4">
              <div className="px-4 mb-2 flex items-center gap-2">
                <span className="atelier-caption">{sectionLabel[key]}</span>
                <span className="h-px flex-1 bg-atelier-line" />
                <span className="atelier-caption tabular-nums">{list.length}</span>
              </div>
              <div className="space-y-0.5 px-2">
                {list.map((conv) => {
                  const isActive = currentConversation?.id === conv.id;
                  return (
                    <div
                      key={conv.id}
                      className={`group relative rounded-lg transition-all duration-200 cursor-pointer overflow-hidden ${
                        isActive
                          ? 'bg-atelier-coral/[0.07]'
                          : 'hover:bg-atelier-surface2'
                      }`}
                    >
                      {/* Left coral rule — active: static, hover: slide in */}
                      <span
                        className={`absolute left-0 top-3 bottom-3 w-[2px] bg-atelier-coral rounded-r transition-transform duration-300 ${
                          isActive ? 'translate-x-0' : '-translate-x-full group-hover:translate-x-0'
                        }`}
                      />
                      {renamingId === conv.id ? (
                        <div className="p-2">
                          <input
                            ref={renameInputRef}
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => onSubmitRename(conv.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') onSubmitRename(conv.id);
                              if (e.key === 'Escape') onCancelRename();
                            }}
                            className="w-full text-[13px] font-medium text-atelier-ink bg-atelier-surface border border-atelier-coral/50 rounded-md px-2 py-1.5 focus:outline-none focus:ring-0"
                            autoFocus
                            data-testid={`rename-input-${conv.id}`}
                          />
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => onLoadConversation(conv.id)}
                            className="w-full text-left px-3 py-2.5 pl-4 pr-12"
                            data-testid={`conversation-${conv.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[13px] truncate leading-tight ${
                                  isActive
                                    ? 'text-atelier-ink'
                                    : 'text-atelier-ink/90 group-hover:text-atelier-ink'
                                } transition-colors`}
                                style={{ fontWeight: isActive ? 600 : 500 }}
                              >
                                {conv.custom_name || conv.agent_name}
                              </span>
                              {conv.is_generating && (
                                <Loader2
                                  className="w-3 h-3 text-atelier-coral animate-spin flex-shrink-0"
                                  data-testid={`conversation-loading-${conv.id}`}
                                />
                              )}
                            </div>
                            <div className="atelier-caption mt-1 tabular-nums">
                              {new Date(conv.updated_at).toLocaleString('zh-TW', {
                                month: 'numeric', day: '2-digit', hour: '2-digit', minute: '2-digit',
                              })}
                            </div>
                          </button>
                          <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all duration-200">
                            <button
                              onClick={(e) => onStartRename(conv, e)}
                              className="w-6 h-6 rounded flex items-center justify-center hover:bg-atelier-surface text-atelier-subtle hover:text-atelier-ink transition-colors"
                              data-testid={`rename-conversation-${conv.id}`}
                            >
                              <Edit className="w-3 h-3" strokeWidth={1.6} />
                            </button>
                            <button
                              onClick={(e) => onDeleteConversation(conv.id, e)}
                              className="w-6 h-6 rounded flex items-center justify-center hover:bg-red-500/10 text-atelier-subtle hover:text-red-500 transition-colors"
                              data-testid={`delete-conversation-${conv.id}`}
                            >
                              <Trash2 className="w-3 h-3" strokeWidth={1.6} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null
        )}

        {conversations.length === 0 && (
          <div className="px-5 py-14 text-center">
            <div className="atelier-oil-loader mx-auto mb-5" style={{ width: 48, height: 48 }} />
            <p className="atelier-caption mb-1.5">No Sessions</p>
            <p className="text-[13px] text-atelier-mute leading-relaxed">
              開始你的第一個對話 —<br />你嘅 AI editor 已經 on duty
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;
